import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
  Slider,
  LinearProgress,
  Dialog,
  IconButton,
  Tooltip,
  Icon,
  Divider,
} from "@mui/material";
import { Theme, styled } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { motion, AnimatePresence } from "framer-motion";
import { LoadingButton } from "@mui/lab";
import { UnitSelector } from "../../components/UnitSelector/UnitSelector";
import { BatchManager } from "../../components/BatchManager/BatchManager";
import { AllergiesSelector } from "../../components/AllergiesSelector/AllergiesSelector";
import { IngredientBuilderSkeleton } from "../../components/IngredientBuilderSkeleton/IngredientBuilderSkeleton";
import {
  RawMaterial,
  Batch,
  ProductFormData,
} from "../../types/inventory.types";
import { inventoryService } from "../../services/inventory.service";
import { toast } from "react-toastify";

// Theme-aware interface for styled components
interface ThemedProps {
  theme: Theme;
}

// Progress and Notification Components
const StyledLinearProgress = styled(LinearProgress)(
  ({ theme }: ThemedProps) => ({
    height: 10,
    borderRadius: 5,
    backgroundColor: alpha(theme.palette.grey[500], 0.12),
  })
);

const NotificationLine = styled(Box)(({ theme }: ThemedProps) => ({
  position: "absolute",
  top: 0,
  width: 2,
  height: "calc(100% + 8px)",
  backgroundColor: theme.palette.warning.main,
  transform: "translateX(-50%)",
  "&::before, &::after": {
    content: '""',
    position: "absolute",
    left: -3,
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: theme.palette.warning.main,
  },
  "&::before": {
    top: -4,
  },
  "&::after": {
    bottom: -4,
  },
}));

// Layout Components
const PageContainer = styled(Box)(({ theme }: ThemedProps) => ({
  padding: theme.spacing(3),
}));

const FormSection = styled(Paper)(({ theme }: ThemedProps) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
  border: "1px solid",
  borderColor: alpha(theme.palette.primary.main, 0.1),
  borderRadius: theme.shape.borderRadius,
  transition: theme.transitions.create(["box-shadow", "transform"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.1)}`,
  },
}));

// Section Components
const SectionWrapper = styled(Box)(({ theme }: ThemedProps) => ({
  marginBottom: theme.spacing(3),
}));

const SectionHeader = styled(Box)(({ theme }: ThemedProps) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
}));

const SectionTitle = styled(Typography)(({ theme }: ThemedProps) => ({
  fontFamily: "Montserrat",
  fontWeight: 600,
  color: theme.palette.primary.main,
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const SectionIcon = styled(Icon)(({ theme }: ThemedProps) => ({
  color: theme.palette.primary.main,
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  fontSize: 24,
  transition: theme.transitions.create(["background-color", "transform"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    transform: "scale(1.05)",
  },
}));

// Content Components
const ContentWrapper = styled(Paper)(({ theme }: ThemedProps) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
  transition: theme.transitions.create(["box-shadow", "transform"], {
    duration: theme.transitions.duration.short,
  }),
  "&:hover": {
    boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.08)}`,
  },
}));

// Form Components
const StyledTextField = styled(TextField)(({ theme }: ThemedProps) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: theme.palette.background.paper,
    transition: theme.transitions.create(
      ["background-color", "box-shadow", "border-color"],
      {
        duration: theme.transitions.duration.short,
      }
    ),
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.02),
    },
    "&.Mui-focused": {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
  "& .MuiInputLabel-root, & .MuiInputBase-input": {
    fontFamily: "Montserrat",
  },
  "& .MuiInputAdornment-root .MuiIcon-root": {
    color: theme.palette.primary.main,
  },
}));

const FormGrid = styled(Grid)(({ theme }: ThemedProps) => ({
  "& .MuiGrid-item": {
    display: "flex",
    flexDirection: "column",
  },
}));

// Action Components
const ActionBar = styled(Box)(({ theme }: ThemedProps) => ({
  marginTop: theme.spacing(4),
  padding: theme.spacing(3, 0),
  borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  display: "flex",
  justifyContent: "flex-end",
  gap: theme.spacing(2),
}));

const IconWrapper = styled(Box)(({ theme }: ThemedProps) => ({
  width: 34,
  height: 34,
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: alpha(theme.palette.primary.main, 0.08),
  color: theme.palette.primary.main,
  transition: theme.transitions.create(["background-color", "transform"], {
    duration: theme.transitions.duration.short,
  }),
  "& .MuiIcon-root": {
    fontSize: 18,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    transform: "scale(1.05)",
  },
}));

const HeaderContent = styled(Box)({
  flex: 1,
});

// Add new styled components after the existing ones
const StockLevelBar = styled(Box)(({ theme }) => ({
  height: 24,
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.grey[200], 0.5),
  position: "relative",
  overflow: "hidden",
}));

const StockIndicator = styled(Box)(({ theme }) => ({
  height: "100%",
  position: "absolute",
  left: 0,
  transition: theme.transitions.create(["width", "background-color"], {
    duration: theme.transitions.duration.standard,
  }),
}));

const StockMarker = styled(Box)(({ theme }) => ({
  position: "absolute",
  width: 2,
  height: "140%",
  top: "-20%",
  backgroundColor: alpha(theme.palette.common.black, 0.2),
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: -4,
    left: -4,
    width: 10,
    height: 10,
    borderRadius: "50%",
    backgroundColor: "inherit",
  },
}));

export const IngredientBuilder: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [useAutoSku, setUseAutoSku] = React.useState(true);
  const [showNoBatchesWarning, setShowNoBatchesWarning] = React.useState(false);
  const [formErrors, setFormErrors] = React.useState({
    name: false,
    category: false,
  });
  const [showStockSettings, setShowStockSettings] = React.useState(false);

  const generateSku = () => {
    const timestamp = new Date().getTime().toString(36);
    const random = Math.random().toString(36).substring(2, 4);
    return `RM-${timestamp}-${random}`.toUpperCase();
  };

  const [rawMaterial, setRawMaterial] = React.useState<RawMaterial>({
    name: "",
    description: "",
    category: "",
    unit: "",
    base_unit: "",
    conversion_factor: 1,
    stock_quantity: 0,
    min_stock_level: 0,
    max_stock_level: 0,
    cost_per_unit: 0,
    supplier: "",
    is_active: true,
    batches: [],
    storage_instructions: "",
    sku: generateSku(),
  });

  // Calculate total stock from batches
  const calculateTotalStock = React.useCallback(() => {
    return rawMaterial?.batches?.reduce(
      (total, batch) => total + (batch.quantity || 0),
      0
    );
  }, [rawMaterial?.batches]);

  // Update stock quantity whenever batches change
  React.useEffect(() => {
    if (rawMaterial?.batches) {
      const totalStock = calculateTotalStock();
      setRawMaterial((prev) => ({
        ...prev,
        stock_quantity: totalStock,
        max_stock_level:
          !prev.max_stock_level || totalStock > prev.max_stock_level
            ? totalStock
            : prev.max_stock_level,
      }));

      if (rawMaterial.batches.length > 0 && !showStockSettings) {
        setShowStockSettings(true);
      }
    }
  }, [rawMaterial?.batches, calculateTotalStock, showStockSettings]);

  React.useEffect(() => {
    if (id) {
      loadRawMaterial();
    }
  }, [id]);

  const loadRawMaterial = async () => {
    try {
      if (!id) return;
      setLoading(true);

      const materialResponse = await inventoryService.getRawMaterial(id, "id");
      const batchesResponse = await inventoryService.listBatches({
        ingredient_id: materialResponse.data.ingredient_id,
        pageIndex: 0,
        pageSize: 100,
        sortField: "receive_date",
        sortOrder: "asc",
        include_wasted: false,
      });

      setRawMaterial({
        ...materialResponse.data,
        batches: batchesResponse.data.batches,
      });
    } catch (error) {
      console.error("Error loading raw material:", error);
      toast.error("Failed to load raw material");
    } finally {
      setLoading(false);
    }
  };

  const handleChange =
    (field: keyof RawMaterial) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;

      // Handle numeric fields
      if (field === "min_stock_level" || field === "max_stock_level") {
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return; // Don't update if not a valid number

        setRawMaterial((prev) => {
          // Ensure min doesn't exceed max and max isn't below min
          if (field === "min_stock_level" && numValue > prev.max_stock_level) {
            return {
              ...prev,
              min_stock_level: prev.max_stock_level,
            };
          }
          if (field === "max_stock_level" && numValue < prev.min_stock_level) {
            return {
              ...prev,
              max_stock_level: prev.min_stock_level,
            };
          }

          return {
            ...prev,
            [field]: numValue,
          };
        });
        return;
      }

      // Handle other fields normally
      setRawMaterial((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleSkuReset = () => {
    setRawMaterial((prev) => ({
      ...prev,
      sku: generateSku(),
    }));
    setUseAutoSku(true);
  };

  const validateForm = () => {
    const errors = {
      name: !rawMaterial.name.trim(),
      category: !rawMaterial.category.trim(),
    };
    setFormErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (rawMaterial.batches.length === 0) {
      setShowNoBatchesWarning(true);
      return;
    }

    try {
      setSaving(true);
      if (id) {
        await inventoryService.updateRawMaterial({
          _id: id,
          ...rawMaterial,
        });
        toast.success("Raw material updated successfully");
      } else {
        await inventoryService.createRawMaterial(
          rawMaterial as unknown as ProductFormData
        );
        toast.success("Raw material created successfully");
      }
      navigate("/business/stock-management/raw-materials");
    } catch (error) {
      toast.error("Failed to save raw material");
    } finally {
      setSaving(false);
    }
  };

  const handleBatchAdd = (batch: Batch) => {
    setRawMaterial((prev) => ({
      ...prev,
      batches: [...prev.batches, batch],
    }));
  };

  const handleBatchUpdate = (updatedBatch: Batch) => {
    console.log("updatedBatch");
    console.log(updatedBatch);
    setRawMaterial((prev) => ({
      ...prev,
      batches: prev.batches.map((batch) =>
        batch.batch_number === updatedBatch.batch_number
          ? { ...batch, ...updatedBatch }
          : batch
      ),
    }));
  };

  const handleBatchDelete = (batchId: string) => {
    setRawMaterial((prev) => ({
      ...prev,
      batches: prev.batches.filter(
        (batch) => batch.id !== batchId && batch.batch_number !== batchId
      ),
    }));
  };

  if (id && loading) {
    return <IngredientBuilderSkeleton />;
  }

  return (
    <PageContainer>
      <PageHeader
        icon="spa"
        title={id ? "Edit Raw Material" : "Create New Raw Material"}
        subtitle="Add or update raw materials with detailed information including batches, allergies, and storage instructions."
      />

      <AnimatePresence>
        <FormSection elevation={0}>
          <SectionHeader>
            <IconWrapper>
              <Icon>info</Icon>
            </IconWrapper>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontFamily: "Montserrat", fontWeight: 600, mb: 0.5 }}
              >
                Basic Information
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "Montserrat" }}
              >
                Enter the essential details of your raw material
              </Typography>
            </Box>
          </SectionHeader>

          <FormGrid container spacing={3}>
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Name"
                value={rawMaterial.name}
                onChange={handleChange("name")}
                required
                error={formErrors.name}
                helperText={formErrors.name ? "Name is required" : ""}
                placeholder="E.g., All-Purpose Flour, Olive Oil"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>inventory</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="Category"
                value={rawMaterial.category}
                onChange={handleChange("category")}
                required
                error={formErrors.category}
                helperText={formErrors.category ? "Category is required" : ""}
                placeholder="E.g., Grains, Oils, Dairy"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>category</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                label="SKU"
                value={rawMaterial.sku}
                onChange={(e) => {
                  setRawMaterial((prev) => ({
                    ...prev,
                    sku: e.target.value,
                  }));
                  if (useAutoSku) setUseAutoSku(false);
                }}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>qr_code</Icon>
                    </InputAdornment>
                  ),
                  endAdornment: useAutoSku ? (
                    <InputAdornment position="end">
                      <Tooltip title="This is an auto-generated SKU">
                        <Icon sx={{ color: "action.active" }}>info</Icon>
                      </Tooltip>
                    </InputAdornment>
                  ) : (
                    <InputAdornment position="end">
                      <Tooltip title="Reset to auto-generated SKU">
                        <IconButton
                          size="small"
                          onClick={handleSkuReset}
                          sx={{
                            color: "#0891B2",
                            "&:hover": {
                              backgroundColor: alpha("#0891B2", 0.08),
                              transform: "translateY(-1px)",
                            },
                          }}
                        >
                          <Icon>refresh</Icon>
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Description"
                value={rawMaterial.description}
                onChange={handleChange("description")}
                multiline
                rows={3}
                placeholder="Describe the raw material, its uses, and any special characteristics"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>description</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </FormGrid>
        </FormSection>

        <FormSection elevation={0}>
          <SectionHeader>
            <IconWrapper>
              <Icon>inventory_2</Icon>
            </IconWrapper>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontFamily: "Montserrat", fontWeight: 600, mb: 0.5 }}
              >
                Batch Management
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "Montserrat" }}
              >
                Manage batches and track inventory levels
              </Typography>
            </Box>
          </SectionHeader>

          <FormGrid container spacing={3}>
            <Grid item xs={12} md={6}>
              <UnitSelector
                value={rawMaterial.unit}
                onChange={(value) =>
                  setRawMaterial((prev) => ({ ...prev, unit: value }))
                }
              />
            </Grid>
          </FormGrid>

          <Box sx={{ mt: 3 }}>
            <BatchManager
              batches={rawMaterial?.batches || []}
              unit={rawMaterial?.unit || ""}
              onBatchAdd={handleBatchAdd}
              onBatchDelete={handleBatchDelete}
              onBatchUpdate={handleBatchUpdate}
              currency="BHD"
            />
          </Box>
        </FormSection>

        <FormSection elevation={0}>
          <SectionHeader>
            <IconWrapper>
              <Icon>inventory</Icon>
            </IconWrapper>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontFamily: "Montserrat", fontWeight: 600, mb: 0.5 }}
              >
                Stock Level Management
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "Montserrat" }}
              >
                Monitor inventory and set notification thresholds
              </Typography>
            </Box>
          </SectionHeader>

          <Box sx={{ mb: 4 }}>
            <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: "Montserrat",
                  color: "text.secondary",
                  minWidth: 140,
                }}
              >
                Current Stock Level:
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                  color: "#0891B2",
                }}
              >
                {rawMaterial.stock_quantity} {rawMaterial.unit}
              </Typography>
            </Box>

            <StockLevelBar>
              <StockIndicator
                sx={{
                  width: `${
                    (rawMaterial.stock_quantity /
                      (rawMaterial.max_stock_level || 1)) *
                    100
                  }%`,
                  bgcolor: (theme) => {
                    const ratio =
                      rawMaterial.stock_quantity /
                      (rawMaterial.max_stock_level || 1);
                    if (ratio <= 0.2) return "#DC2626";
                    if (ratio <= 0.4) return "#F59E0B";
                    return "#059669";
                  },
                }}
              />
              {rawMaterial.min_stock_level > 0 && (
                <StockMarker
                  sx={{
                    left: `${
                      (rawMaterial.min_stock_level /
                        (rawMaterial.max_stock_level || 1)) *
                      100
                    }%`,
                    backgroundColor: "#F59E0B",
                  }}
                />
              )}
            </StockLevelBar>

            <Box
              sx={{ mt: 1, display: "flex", justifyContent: "space-between" }}
            >
              <Typography
                variant="caption"
                sx={{ fontFamily: "Montserrat", color: "text.secondary" }}
              >
                0 {rawMaterial.unit}
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontFamily: "Montserrat", color: "text.secondary" }}
              >
                Max: {rawMaterial.max_stock_level} {rawMaterial.unit}
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                type="number"
                label={`Notify me when stock is below`}
                value={rawMaterial.min_stock_level}
                onChange={handleChange("min_stock_level")}
                inputProps={{
                  min: 0,
                  step: "any",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>notifications</Icon>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Montserrat",
                          color: "text.secondary",
                        }}
                      >
                        {rawMaterial.unit}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                helperText="You'll receive alerts when stock drops below this level"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledTextField
                fullWidth
                type="number"
                label="Maximum Stock Capacity"
                value={rawMaterial.max_stock_level}
                onChange={handleChange("max_stock_level")}
                inputProps={{
                  min: 0,
                  step: "any",
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>inventory_2</Icon>
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "Montserrat",
                          color: "text.secondary",
                        }}
                      >
                        {rawMaterial.unit}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                helperText="Maximum amount you can store"
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Montserrat",
                color: "text.secondary",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ fontSize: 18 }}>info</Icon>
              Smart inventory management:
            </Typography>
            <Box
              component="ul"
              sx={{
                mt: 1,
                pl: 4,
                "& li": {
                  color: "text.secondary",
                  fontFamily: "Montserrat",
                  fontSize: "0.875rem",
                  mb: 0.5,
                },
              }}
            >
              <li>Get notified when stock needs replenishing</li>
              <li>Prevent overstocking with capacity limits</li>
              <li>Stock levels update automatically as batches change</li>
            </Box>
          </Box>
        </FormSection>

        <FormSection elevation={0}>
          <SectionHeader>
            <IconWrapper>
              <Icon>warning</Icon>
            </IconWrapper>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontFamily: "Montserrat", fontWeight: 600, mb: 0.5 }}
              >
                Allergen Information
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "Montserrat" }}
              >
                Specify any allergens present in this raw material
              </Typography>
            </Box>
          </SectionHeader>

          <AllergiesSelector
            selectedAllergies={rawMaterial.allergens || []}
            onChange={(allergens) =>
              setRawMaterial((prev) => ({ ...prev, allergens }))
            }
            defaultExpanded={true}
          />
        </FormSection>

        <FormSection elevation={0}>
          <SectionHeader>
            <IconWrapper>
              <Icon>warehouse</Icon>
            </IconWrapper>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontFamily: "Montserrat", fontWeight: 600, mb: 0.5 }}
              >
                Additional Information
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "Montserrat" }}
              >
                Add storage instructions and other important details
              </Typography>
            </Box>
          </SectionHeader>

          <FormGrid container spacing={3}>
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Storage Instructions"
                value={rawMaterial.storage_instructions}
                onChange={handleChange("storage_instructions")}
                multiline
                rows={3}
                placeholder="Specify storage requirements, temperature, humidity, etc."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon>warehouse</Icon>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </FormGrid>
        </FormSection>

        <ActionBar>
          <Button
            variant="outlined"
            onClick={() => navigate("/business/stock-management/raw-materials")}
            startIcon={<Icon>arrow_back</Icon>}
            sx={{
              borderColor: (theme) => alpha(theme.palette.primary.main, 0.5),
              color: (theme) => theme.palette.primary.main,
              "&:hover": {
                borderColor: (theme) => theme.palette.primary.main,
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.04),
              },
              fontFamily: "Montserrat",
              fontWeight: 600,
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            onClick={handleSave}
            loading={saving}
            startIcon={<Icon>save</Icon>}
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              "&:hover": {
                backgroundColor: (theme) => theme.palette.primary.dark,
              },
              fontFamily: "Montserrat",
              fontWeight: 600,
            }}
          >
            {id ? "Update Raw Material" : "Create Raw Material"}
          </LoadingButton>
        </ActionBar>

        {/* Warning Dialog */}
        <Dialog
          open={showNoBatchesWarning}
          onClose={() => setShowNoBatchesWarning(false)}
          PaperProps={{
            sx: {
              borderRadius: 2,
              maxWidth: 450,
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Icon sx={{ fontSize: 48, color: "warning.main", mb: 2 }}>
                warning_amber
              </Icon>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ fontFamily: "Montserrat", fontWeight: 600 }}
              >
                No Batches Added
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: "Montserrat" }}
              >
                You haven't added any batches to this raw material. Batches are
                important for tracking inventory and costs.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setShowNoBatchesWarning(false)}
                startIcon={<Icon>arrow_back</Icon>}
                sx={{
                  borderColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.5),
                  color: (theme) => theme.palette.primary.main,
                  "&:hover": {
                    borderColor: (theme) => theme.palette.primary.main,
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.04),
                  },
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                }}
              >
                Go Back
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  setShowNoBatchesWarning(false);
                  handleSave();
                }}
                startIcon={<Icon>save</Icon>}
                sx={{
                  backgroundColor: (theme) => theme.palette.warning.main,
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.warning.dark,
                  },
                  fontFamily: "Montserrat",
                  fontWeight: 600,
                }}
              >
                Save Anyway
              </Button>
            </Box>
          </Box>
        </Dialog>
      </AnimatePresence>
    </PageContainer>
  );
};
